import type { CapitalContributionsConfig, MemberCode } from '@/types/waterfall'
import { MEMBERS } from '@/types/waterfall'
import {
  formatCurrencyField,
  parseDistributionAmount,
} from '@/utils/waterfallCalculator'

export type ContributionEditorRow = { date: string; member: MemberCode; amount: string }

export type ContributionReadOnlyRow = {
  date: string
  member: MemberCode
  amount: number
  runningTotal: number
  memberRunningTotal: number
}

export function contributionsFromConfig(value: unknown): ContributionEditorRow[] {
  if (!value || typeof value !== 'object') return []
  const rows: ContributionEditorRow[] = []
  for (const [date, byMember] of Object.entries(
    value as Record<string, Record<string, number>>,
  )) {
    if (!byMember || typeof byMember !== 'object') continue
    for (const [member, amount] of Object.entries(byMember)) {
      if (!MEMBERS.includes(member as MemberCode)) continue
      rows.push({
        date,
        member: member as MemberCode,
        amount: formatCurrencyField(Number(amount)),
      })
    }
  }
  return rows.sort((a, b) => a.date.localeCompare(b.date) || a.member.localeCompare(b.member))
}

export function contributionsToConfig(rows: ContributionEditorRow[]): CapitalContributionsConfig {
  const out: CapitalContributionsConfig = {}
  for (const row of rows) {
    if (!row.date) continue
    const parsed = parseDistributionAmount(row.amount)
    if (parsed == null || Math.abs(parsed) < 0.005) continue
    if (!out[row.date]) out[row.date] = {}
    out[row.date]![row.member] = parsed
  }
  return out
}

export function contributionsReadOnlyRowsFromConfig(value: unknown): ContributionReadOnlyRow[] {
  const sorted = contributionsFromConfig(value)
  let runningTotal = 0
  const memberRunning: Record<MemberCode, number> = MEMBERS.reduce(
    (acc, m) => {
      acc[m] = 0
      return acc
    },
    {} as Record<MemberCode, number>,
  )

  return sorted.map((row) => {
    const amount = parseDistributionAmount(row.amount) ?? 0
    runningTotal += amount
    memberRunning[row.member] += amount
    return {
      date: row.date,
      member: row.member,
      amount,
      runningTotal,
      memberRunningTotal: memberRunning[row.member],
    }
  })
}

export function memberContributionTotals(
  rows: ContributionReadOnlyRow[],
): Record<MemberCode, number> {
  const totals = MEMBERS.reduce(
    (acc, m) => {
      acc[m] = 0
      return acc
    },
    {} as Record<MemberCode, number>,
  )
  for (const row of rows) {
    totals[row.member] += row.amount
  }
  return totals
}

export function overallContributionTotal(rows: ContributionReadOnlyRow[]): number {
  return rows.reduce((sum, row) => sum + row.amount, 0)
}
